import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { BookOpen, ArrowRight, X } from 'lucide-react';
import { CrescentStarIcon } from '../common/IslamicIcons';

/**
 * ContinueReadingCard — shown when user has a last-read position.
 * Displayed on the QuranPage above the tab content.
 */
const ContinueReadingCard = ({ lastReadEntry, chapterId, onDismiss }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();

  if (!lastReadEntry) return null;

  const [chapter_id, position] = lastReadEntry;
  const { chapter_name, verse_key, verse_number } = position;

  const handleContinue = () => {
    navigate(`/quran/surah/${chapter_id}?resume=${verse_key}`);
  };

  return (
    <div
      data-testid="continue-reading-card"
      className={`relative rounded-3xl overflow-hidden animate-fade-up ${
        isDarkMode ? 'border border-white/10' : 'border border-[var(--p-hex)]/15 shadow-lg'
      }`}
      style={{
        background: isDarkMode
          ? 'linear-gradient(135deg, var(--card-dark-from), var(--card-dark-to))'
          : 'linear-gradient(135deg, var(--bg-light-via), #ffffff)',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Ambient palette glow */}
      <div className="absolute -right-10 -top-10 w-36 h-36 rounded-full pointer-events-none"
        style={{ background: isDarkMode ? '#f59e0b' : 'var(--p-hex)', opacity: isDarkMode ? 0.18 : 0.12, filter: 'blur(34px)' }} />

      <div className="relative z-10 p-4">
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Icon */}
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: isDarkMode ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.14)' }}>
            <BookOpen className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
          </div>

          {/* Info */}
          <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
            <p className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
              {t('quranReader.continueReading', 'Continue Reading')}
            </p>
            <p className={`text-base font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {chapter_name}
            </p>
            <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} dir="ltr">
              {t('quranReader.lastRead', 'Last read')} · {verse_key}
            </p>
          </div>

          {/* Dismiss */}
          <button
            data-testid="dismiss-continue"
            onClick={onDismiss}
            className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
              isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-black/5 text-gray-500 hover:bg-black/10'
            }`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Full-width resume CTA */}
        <button
          data-testid="continue-btn"
          onClick={handleContinue}
          className={`mt-3.5 w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm font-bold transition-colors active:scale-[0.99] ${
            isDarkMode ? 'bg-amber-500 text-[#0f172a] hover:bg-amber-400' : 'bg-amber-500 text-white hover:bg-amber-600 shadow-md shadow-amber-500/25'
          }`}
        >
          {t('quranReader.resumeBtn', 'Resume')}
          <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default ContinueReadingCard;
